import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Download, FileText, Loader2 } from "lucide-react";

// Public FRA/FFC listing for one institute. Groups documents by academic year (latest first).
export default function FraFfcPublicPage({ slug, headingClass = "font-serif text-3xl text-slate-900", accentClass = "text-[#234333]" }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instName, setInstName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/website/${slug}`);
        setDocs(data?.fra_ffc_documents || []);
        setInstName(data?.institute?.name || "");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const groups = useMemo(() => {
    const map = {};
    for (const d of docs) {
      (map[d.academic_year] = map[d.academic_year] || []).push(d);
    }
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [docs]);

  const proxiedUrl = (d) => {
    const u = d.pdf_url || "";
    return u.startsWith("http") ? u : u;
  };

  return (
    <div className="max-w-5xl mx-auto py-12 px-4" data-testid="fra-ffc-public-page">
      <div className="text-center mb-10">
        <FileText size={28} className={`mx-auto mb-3 ${accentClass}`} />
        <h1 className={headingClass}>FRA / FFC</h1>
        <p className="text-slate-500 mt-2 text-sm">
          Fees Regulating Authority (FRA) & Fees Fixation Committee (FFC) {instName ? `· ${instName}` : ""}
        </p>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-500" data-testid="fra-ffc-loading">
          <Loader2 className="animate-spin inline" /> Loading…
        </div>
      ) : groups.length === 0 ? (
        <div
          className="py-16 text-center text-slate-500 border border-dashed rounded-md"
          data-testid="fra-ffc-empty"
        >
          No FRA/FFC documents published yet.
        </div>
      ) : (
        <div className="space-y-8" data-testid="fra-ffc-groups">
          {groups.map(([year, items]) => (
            <div key={year} className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <div className={`px-5 py-3 bg-slate-50 border-b border-slate-200 ${accentClass} font-semibold tracking-wide uppercase text-xs`}>
                Academic Year {year}
              </div>
              <ul className="divide-y divide-slate-100">
                {items.map((d) => (
                  <li
                    key={d.id}
                    data-testid={`fra-ffc-doc-${d.id}`}
                    className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-slate-50/60"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText size={16} className="text-slate-400 shrink-0" />
                      <span className="text-sm text-slate-800 truncate">{d.title}</span>
                    </div>
                    <a
                      href={proxiedUrl(d)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`fra-ffc-download-${d.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs tracking-wide uppercase border border-slate-300 hover:bg-slate-100 rounded-sm shrink-0"
                    >
                      <Download size={12} /> Download
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
